import ItemList from 'flarum/common/utils/ItemList';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Dropdown from 'flarum/common/components/Dropdown';
import IndexPage from 'flarum/forum/components/IndexPage';
import app from 'flarum/forum/app';
import EventCard from './EventCard';
import EventFormModal from './EventFormModal';
import EventSubscriptionButton from './EventSubscriptionButton';
import CalendarSyncModal from './CalendarSyncModal';

// Module-level state so it survives re-renders and is not affected by IndexPage's lifecycle
const state = {
  tab: 'upcoming',
  sort: 'eventDate',
  loading: true,
  events: [],
};

export default class EventsPage extends IndexPage {
  oninit(vnode) {
    super.oninit(vnode);

    const title = app.translator.trans('florcarioca-rsvp.forum.nav.events');
    app.history.push('index', title);
    app.setTitle(title);

    state.tab = 'upcoming';
    state.sort = 'eventDate';
    state.loading = true;
    state.events = [];
    this.loadEvents();
  }

  sortOptions() {
    const t = (key) => app.translator.trans('florcarioca-rsvp.forum.events_page.' + key);
    return {
      eventDate:    t('sort_soonest'),
      '-eventDate': t('sort_furthest'),
    };
  }

  loadEvents() {
    let params;
    if (state.tab === 'past') {
      const sort = state.sort === 'eventDate' ? '-eventDate' : 'eventDate';
      params = { filter: { past: 1 }, sort };
    } else {
      params = { filter: { active: 1 }, sort: state.sort };
    }

    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/events',
      params,
    }).then((data) => {
      const pushed = app.store.pushPayload(data);
      state.events = Array.isArray(pushed) ? pushed : (pushed ? [pushed] : []);
      state.loading = false;
      m.redraw();
    }).catch(() => {
      state.loading = false;
      m.redraw();
    });
  }

  currentTag() {
    return null;
  }

  sidebarItems() {
    const items = super.sidebarItems();

    items.remove('newDiscussion');

    if (this.canCreateEvents()) {
      items.add(
        'createEvent',
        <Button
          icon="fas fa-plus"
          className="Button Button--primary IndexPage-newDiscussion"
          itemClassName="App-primaryControl"
          onclick={() => app.modal.show(EventFormModal, { oncreate: () => this.loadEvents() })}
        >
          {app.translator.trans('florcarioca-rsvp.forum.events_page.create')}
        </Button>,
        100
      );
    }

    if (app.session.user) {
      items.add(
        'eventSubscription',
        <div className="EventsPage-subscription">
          <EventSubscriptionButton />
        </div>,
        50
      );

      items.add(
        'calendarSync',
        <Button
          icon="fas fa-calendar-alt"
          className="Button"
          onclick={() => app.modal.show(CalendarSyncModal)}
        >
          {app.translator.trans('florcarioca-rsvp.forum.calendar.sync_button')}
        </Button>,
        40
      );
    }

    return items;
  }

  setTab(tab) {
    state.tab = tab;
    state.sort = 'eventDate';
    state.loading = true;
    state.events = [];
    this.loadEvents();
  }

  setSort(key) {
    state.sort = key;
    state.loading = true;
    state.events = [];
    this.loadEvents();
  }

  contentItems() {
    const items = new ItemList();
    const t = (key) => app.translator.trans('florcarioca-rsvp.forum.events_page.' + key);
    const options = this.sortOptions();

    const sortButtons = [
      { key: 'eventDate',   label: options['eventDate'] },
      { key: '-eventDate',  label: options['-eventDate'] },
    ];

    const tabs = [
      { key: 'upcoming', label: t('tab_upcoming') },
      { key: 'past',     label: t('tab_past') },
    ];

    items.add(
      'toolbar',
      <div className="IndexPage-toolbar EventsPage-toolbar">
        <ul className="IndexPage-toolbar-view">
          <li>
            <div className="EventsPage-tabs">
              {tabs.map((tab) => (
                <Button
                  className={'Button EventsPage-tab ' + (state.tab === tab.key ? 'active' : '')}
                  onclick={this.setTab.bind(this, tab.key)}
                >
                  {tab.label}
                </Button>
              ))}
            </div>
          </li>
          <li>
            <Dropdown
                buttonClassName="Button"
                label={options[state.sort] || sortButtons[0].label}
              >
                {sortButtons.map((opt) => (
                  <Button
                    className={'Button ' + (state.sort === opt.key ? 'active' : '')}
                    onclick={this.setSort.bind(this, opt.key)}
                  >
                    {opt.label}
                  </Button>
                ))}
            </Dropdown>
          </li>
        </ul>
      </div>,
      100
    );

    const emptyKey = state.tab === 'past' ? 'empty_past' : 'empty';

    items.add(
      'events',
      <div>
        <div className="EventsPage-header">
          <h2>
            <i className="fas fa-calendar-alt"></i>{' '}
            {app.translator.trans('florcarioca-rsvp.forum.events_page.title')}
          </h2>
        </div>
        <div className="EventsPage-list">
          {state.loading ? (
            <LoadingIndicator />
          ) : state.events.length === 0 ? (
            <p className="EventsPage-empty">{t(emptyKey)}</p>
          ) : (
            state.events.map((event) => <EventCard event={event} key={event.id()} />)
          )}
        </div>
      </div>,
      90
    );

    return items;
  }

  canCreateEvents() {
    return app.session.user && app.forum.attribute('canCreateEvents');
  }
}
